/**
 * Validation utilities for the application
 */

/**
 * Safely parse a number from string or number input
 * Returns 0 if the value cannot be parsed
 */
export const safeParseNumber = (value: string | number | undefined | null): number => {
  if (value === null || value === undefined) return 0
  
  // If it's already a number, validate it
  if (typeof value === 'number') {
    return isNaN(value) || !isFinite(value) ? 0 : value
  }
  
  // Convert to string and clean
  const stringValue = String(value).trim()
  if (stringValue === '') return 0
  
  // Remove non-numeric characters except decimal point and minus
  const cleanedValue = stringValue.replace(/[^\d.-]/g, '')
  
  // Parse the number
  const parsed = parseFloat(cleanedValue)
  
  // Validate the result
  return isNaN(parsed) || !isFinite(parsed) ? 0 : parsed
}

/**
 * Safely parse an integer from string or number input
 * Returns 0 if the value cannot be parsed
 */
export const safeParseInt = (value: string | number | undefined | null): number => {
  const parsed = safeParseNumber(value)
  return Math.round(parsed)
}

/**
 * Validate a percentage value (0-100)
 */
export const validatePercentage = (value: number): number => {
  if (isNaN(value) || !isFinite(value)) return 0
  return Math.max(0, Math.min(100, value))
}

/**
 * Validate a currency amount (non-negative)
 */
export const validateCurrency = (value: number): number => {
  if (isNaN(value) || !isFinite(value)) return 0
  return Math.max(0, value)
}

/**
 * Parse and validate a number from form input
 */
export const parseFormNumber = (value: string): number => {
  // Handle Swedish decimal separator
  const normalizedValue = value.replace(',', '.')
  return safeParseNumber(normalizedValue)
}

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate Swedish personal number (personnummer)
 */
export const validatePersonnummer = (personnummer: string): boolean => {
  // Remove any hyphens or spaces
  const cleaned = personnummer.replace(/[-\s]/g, '')
  
  // Check if it's 10 or 12 digits
  if (!/^\d{10}$|^\d{12}$/.test(cleaned)) {
    return false
  }
  
  // TODO: Add Luhn algorithm validation for Swedish personal numbers
  return true
}

/**
 * Validate Swedish organization number
 */
export const validateOrganizationNumber = (orgNumber: string): boolean => {
  // Remove any hyphens or spaces
  const cleaned = orgNumber.replace(/[-\s]/g, '')
  
  // Check if it's 10 digits
  if (!/^\d{10}$/.test(cleaned)) {
    return false
  }
  
  // TODO: Add proper validation for Swedish organization numbers
  return true
}

/**
 * Validate date range
 */
export const validateDateRange = (startDate: Date, endDate: Date): boolean => {
  return startDate <= endDate
}

/**
 * Validate file type for upload
 */
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''
  return allowedTypes.includes(fileExtension)
}

/**
 * Validate file size (in bytes)
 */
export const validateFileSize = (file: File, maxSizeInBytes: number): boolean => {
  return file.size <= maxSizeInBytes
}